import {createSlice, PayloadAction} from '@reduxjs/toolkit'

/**
 * Slice de Redux para gestionar mensajes de estado en la interfaz de usuario.
 *
 * Este slice maneja los mensajes de éxito, error y advertencia que se muestran en la interfaz de usuario,
 * junto con el tipo de mensaje correspondiente y el modo de visualización (modal o alerta). Es útil para
 * dar retroalimentación al usuario sobre las acciones realizadas.
 *
 * Incluye varios reducers para establecer mensajes de diferentes tipos y modos de visualización:
 * - `setSuccessMessage`, `setErrorMessage`, `setWarningMessage`: Establecen mensajes de alerta.
 * - `setSuccessModalMessage`, `setErrorModalMessage`, `setWarningModalMessage`: Establecen mensajes de modal.
 * - `cleanSuccessMessage`: Limpia cualquier mensaje y sus propiedades, restableciéndolos a `undefined`.
 *
 * @example
 * // Uso del reducer para establecer un mensaje de éxito en modal
 * dispatch(setSuccessModalMessage({ message: 'Operación completada con éxito', description: 'Detalle adicional' }));
 *
 * // Uso del reducer para limpiar el mensaje
 * dispatch(cleanSuccessMessage());
 */

export interface MessageState {
    title: string | undefined
    description: string | undefined
    messageType: 'success' | 'danger' | 'warning' | undefined
    displayType?: 'modal' | 'alert' | undefined
}

const initialState: MessageState = {
    title: undefined,
    description: undefined,
    messageType: undefined,
    displayType: undefined,
}

interface IPayloadActionMessage {
    title?: string
    description?: string
}

export const messageSlice = createSlice({
    name: 'userSessionRedux',
    initialState,
    reducers: {
        setSuccessMessage: (state, action: PayloadAction<IPayloadActionMessage>) => {
            state.title = action.payload.title
            state.description = action.payload.description
            state.messageType = 'success'
            state.displayType = 'alert'
        },
        setErrorMessage: (state, action: PayloadAction<IPayloadActionMessage>) => {
            state.title = action.payload.title
            state.description = action.payload.description
            state.messageType = 'danger'
            state.displayType = 'alert'
        },
        setWarningMessage: (state, action: PayloadAction<IPayloadActionMessage>) => {
            state.title = action.payload.title
            state.description = action.payload.description
            state.messageType = 'warning'
            state.displayType = 'alert'
        },
        setSuccessModalMessage: (state, action: PayloadAction<IPayloadActionMessage>) => {
            state.title = action.payload.title
            state.description = action.payload.description
            state.messageType = 'success'
            state.displayType = 'modal'
        },
        setErrorModalMessage: (state, action: PayloadAction<IPayloadActionMessage>) => {
            state.title = action.payload.title
            state.description = action.payload.description
            state.messageType = 'danger'
            state.displayType = 'modal'
        },
        setWarningModalMessage: (state, action: PayloadAction<IPayloadActionMessage>) => {
            state.title = action.payload.title
            state.description = action.payload.description
            state.messageType = 'warning'
            state.displayType = 'modal'
        },
        cleanSuccessMessage: (state) => {
            state.title = undefined
            state.description = undefined
            state.messageType = undefined
            state.displayType = undefined
        },
    },
})

export const {
    setSuccessMessage,
    setErrorMessage,
    setWarningMessage,
    setSuccessModalMessage,
    setWarningModalMessage,
    setErrorModalMessage,
    cleanSuccessMessage,
} = messageSlice.actions
export default messageSlice.reducer
